import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './form-modal.html';
import type { FormDefinition, FormFieldType, TagGroup } from '../services/api.types';
import { createForm, listTagGroups, updateForm } from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';

type EditableField = {
  id?: string;
  name: string;
  dataType: FormFieldType;
  tagGroupId: string;
  addTagToDocument: boolean;
  uniqueForDocument: boolean;
};

const FIELD_TYPES: { value: FormFieldType; labelKey: string }[] = [
  { value: 'text', labelKey: 'forms.fieldTypes.text' },
  { value: 'number', labelKey: 'forms.fieldTypes.number' },
  { value: 'date', labelKey: 'forms.fieldTypes.date' },
  { value: 'boolean', labelKey: 'forms.fieldTypes.boolean' },
  { value: 'tagSelect', labelKey: 'forms.fieldTypes.tagSelect' },
];

@customElement({ name: 'form-modal', template })
export class FormModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (form: FormDefinition) => void;
  @bindable public closed!: () => void;
  @bindable public formToEdit: FormDefinition | null = null;

  public title = '';
  public isActive = true;
  public fields: EditableField[] = [];
  public tagGroups: TagGroup[] = [];
  public readonly fieldTypes = FIELD_TYPES;
  public saving = false;
  public loadingTagGroups = true;
  public error = '';

  private _keyHandler!: (event: KeyboardEvent) => void;
  private _isBound = false;
  private _initialTitle = '';
  private _initialIsActive = true;
  private _initialFields: EditableField[] = [];
  private _closeConfirmationOpen = false;

  public get isEditMode(): boolean {
    return this.formToEdit !== null;
  }

  public get hasChanges(): boolean {
    return this.title !== this._initialTitle ||
      this.isActive !== this._initialIsActive ||
      JSON.stringify(this.fields) !== JSON.stringify(this._initialFields);
  }

  public binding(): void {
    this._isBound = true;
    this.resetFromBinding();
  }

  public attached(): void {
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') void this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
    void this.loadTagGroups();
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public formToEditChanged(): void {
    if (!this._isBound) return;
    this.resetFromBinding();
  }

  public async close(): Promise<void> {
    if (this.saving || this._closeConfirmationOpen) return;
    if (this.hasChanges) {
      this._closeConfirmationOpen = true;
      try {
        const discard = await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'),
        );
        if (!discard) return;
      } finally {
        this._closeConfirmationOpen = false;
      }
    }
    this.closed();
  }

  public addField(): void {
    this.fields = [
      ...this.fields,
      {
        name: '',
        dataType: 'text',
        tagGroupId: '',
        addTagToDocument: false,
        uniqueForDocument: false,
      },
    ];
  }

  public removeField(index: number): void {
    this.fields = this.fields.filter((_field, fieldIndex) => fieldIndex !== index);
  }

  public moveField(index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= this.fields.length) return;
    const fields = [...this.fields];
    const [field] = fields.splice(index, 1);
    fields.splice(targetIndex, 0, field);
    this.fields = fields;
  }

  public onFieldTypeChanged(field: EditableField): void {
    if (field.dataType !== 'tagSelect') {
      field.tagGroupId = '';
      field.addTagToDocument = false;
    }
  }

  public tagGroupName(groupId: string): string {
    return this.tagGroups.find(group => group.id === groupId)?.name ?? '';
  }

  public async save(): Promise<void> {
    this.error = '';
    const normalizedTitle = this.title.trim();
    if (!normalizedTitle) {
      this.error = this.i18n.tr('forms.form.titleRequired');
      return;
    }
    if (this.fields.length === 0) {
      this.error = this.i18n.tr('forms.form.fieldRequired');
      return;
    }

    const names = new Set<string>();
    for (const [index, field] of this.fields.entries()) {
      field.name = field.name.trim();
      if (!field.name) {
        this.error = this.i18n.tr('forms.form.fieldNameRequired', { index: index + 1 });
        return;
      }
      const normalizedName = field.name.toLowerCase();
      if (!names.add(normalizedName)) {
        this.error = this.i18n.tr('forms.form.duplicateFieldName', { name: field.name });
        return;
      }
      if (field.dataType === 'tagSelect' && !field.tagGroupId) {
        this.error = this.i18n.tr('forms.form.tagGroupRequired', { name: field.name });
        return;
      }
    }

    this.saving = true;
    try {
      const dto = {
        title: normalizedTitle,
        isActive: this.isActive,
        fields: this.fields.map(field => ({
          id: field.id,
          name: field.name,
          dataType: field.dataType,
          tagGroupId: field.dataType === 'tagSelect' ? field.tagGroupId : null,
          addTagToDocument: field.dataType === 'tagSelect' && field.addTagToDocument,
          uniqueForDocument: field.uniqueForDocument,
        })),
      };
      const form = this.isEditMode && this.formToEdit
        ? await updateForm(this.formToEdit.id, dto)
        : await createForm(dto);
      this.saved(form);
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.saving = false;
    }
  }

  private resetFromBinding(): void {
    this.title = this.formToEdit?.title ?? '';
    this.isActive = this.formToEdit?.isActive ?? true;
    this.fields = this.formToEdit?.fields
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(field => ({
        id: field.id,
        name: field.name,
        dataType: field.dataType,
        tagGroupId: field.tagGroupId ?? '',
        addTagToDocument: field.addTagToDocument ?? false,
        uniqueForDocument: field.uniqueForDocument ?? false,
      })) ?? [];
    if (this.fields.length === 0) this.addField();
    this.error = '';
    this._initialTitle = this.title;
    this._initialIsActive = this.isActive;
    this._initialFields = this.fields.map(field => ({ ...field }));
  }

  private async loadTagGroups(): Promise<void> {
    this.loadingTagGroups = true;
    try {
      this.tagGroups = await listTagGroups();
      this.fields = this.fields.map(field => ({ ...field }));
    } catch (error: unknown) {
      this.error = error instanceof Error
        ? error.message
        : this.i18n.tr('forms.loadTagGroupsFailed');
    } finally {
      this.loadingTagGroups = false;
    }
  }
}
